import {
  type ApplicationCancellationReasonEnum,
  GigAttachmentTypeEnum,
  type GigCancellationReasonEnum,
  SkippedBreakReasonEnum,
  TipPolicyEnum,
} from "@sidegigllc/api/client";
import * as z from "zod/mini";

const pageFromUrl = (url: string | null) => (url ? Number(new URL(url).searchParams.get("page")) : null);

const paginationFields = z.object({
  count: z.number().check(z.nonnegative()),
  next: z.pipe(z.nullable(z.string()), z.transform(pageFromUrl)),
  previous: z.pipe(z.nullable(z.string()), z.transform(pageFromUrl)),
});
export type PaginationResponse<T> = z.infer<typeof paginationFields> & { results: Array<T> };

export const billingSourceSchema = z.enum(["LOCATION", "ORGANIZATION"]);
export type BillingSource = z.infer<typeof billingSourceSchema>;

export const employmentTypeSchema = z.enum(["W2", "F1099"]);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

export const gigStatusSchema = z.enum([
  "UNAPPROVED",
  "CANCELLED",
  "POSTED",
  "CONFIRMED",
  "UNFILLED",
  "INCOMPLETE",
  "FAILED_PAYMENT",
  "COMPLETED",
]);
export const gigStatuses = gigStatusSchema.options;
/**
 * Schema for gig statuses.
 * UNAPPROVED: Posted from an unapproved location.
 * CANCELLED: Cancelled (whether finished or not).
 * POSTED: Hasn't finished, nobody confirmed.
 * CONFIRMED: Hasn't finished, all pros confirmed.
 * UNFILLED: Finished, nobody confirmed.
 * INCOMPLETE: Finished, confirmed, not completed.
 * FAILED_PAYMENT: Finished, confirmed, failed payment.
 * COMPLETED: Finished, confirmed, completed.
 */
export type GigStatus = z.infer<typeof gigStatusSchema>;

export const gigTypeSchema = z.enum([
  "MARKETPLACE",
  "PRIVATE_DIRECT_OFFER",
  "INTERVIEW",
  "NETWORK",
  "WORKING_INTERVIEW",
]);
export const gigTypes = gigTypeSchema.options;
/**
 * Schema for gig types.
 * MARKETPLACE: A classic gig open to the gigpro Marketplace.
 * PRIVATE_DIRECT_OFFER: A private gig offered to a specific pro in the Marketplace.
 * INTERVIEW: A hire interview.
 * NETWORK: A gig posted to network Pros, not available in the Marketplace.
 * WORKING_INTERVIEW: A private shift of working interviews for a job posting.
 */
export type GigType = z.infer<typeof gigTypeSchema>;

/**
 * "Does this shift include tips?" answer (WORK-8452, pooled follow-up WORK-9422). `null` on the wire means
 * unanswered. INCLUDED: the shift includes tips. NOT_INCLUDED: it does not. POOLED: tips pooled across staff.
 */
export const tipPolicySchema = z.enum(TipPolicyEnum);
export type TipPolicy = z.infer<typeof tipPolicySchema>;

/** What a business and a pro read for each answer. */
export const TIP_POLICY_LABELS: Record<TipPolicy, string> = {
  INCLUDED: "Includes tips",
  NOT_INCLUDED: "No tips",
  POOLED: "Pooled tips",
};

/** Review/detail copy for a `tip_policy` answer; `null` when unanswered (render nothing). */
export function getTipPolicyLabel(tipPolicy: TipPolicy | null | undefined) {
  return tipPolicy == null ? null : TIP_POLICY_LABELS[tipPolicy];
}

export const shiftBreakComplianceSchema = z.enum([
  "COMPLIANT",
  "NON_COMPLIANT",
  "NOT_APPLICABLE",
  "TOO_LONG",
  "TOO_SHORT",
  "TOO_LATE",
  "SKIPPED",
  "UNKNOWN",
  "NO_START_END_TIME",
  "SHORT_SHIFT",
]);
export const shiftBreakFieldsSchema = z.object({
  id: z.string(),
  minutes: z.nullish(z.number()),
  // seconds are the whole seconds of the break, not just the seconds part of the minutes
  seconds: z.nullish(z.number()),
  start_time: z.nullish(z.coerce.date()),
  end_time: z.nullish(z.coerce.date()),
  compliance_status: shiftBreakComplianceSchema,
  // Worker-entered non-compliance reason (short category) + free-text explanation (≤500 chars).
  // `skipped_break_reason` ships today; `skipped_break_explanation` is added by the CORE-6163
  // backend and lights up on the next API-client sync. Both nullish so this is safe meanwhile.
  skipped_break_reason: z.nullish(z.union([z.enum(SkippedBreakReasonEnum), z.literal("")])),
  skipped_break_explanation: z.nullish(z.string()),
});
export const shiftBreakSchema = z.pipe(
  shiftBreakFieldsSchema,
  z.transform((data) => {
    const minutes = data.minutes ?? 0;
    const seconds = data.seconds ?? 0;

    const hoursFormatted = `${Math.floor(minutes / 60)}`.padStart(2, "0");
    const minutesFormatted = `${minutes % 60}`.padStart(2, "0");
    const secondsFormatted = `${seconds - minutes * 60}`.padStart(2, "0");

    return {
      ...data,
      minutes,
      seconds,
      // Format the duration as HH:MM:SS
      durationFormatted: `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`,
    };
  }),
);
export type ShiftBreak = z.infer<typeof shiftBreakSchema>;

export const approvalStatusSchema = z.enum(["APPROVED", "PENDING", "DENIED", "FINANCE"]);
export const approvalStatusOptions = approvalStatusSchema.options;
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

export const parkingExpenseTypeSchema = z.enum(["FREE", "REIMBURSED", "NOT_REIMBURSED"]);
export type ParkingExpenseType = z.infer<typeof parkingExpenseTypeSchema>;
export const parkingExpenseTypeLabels: Record<ParkingExpenseType, string> = {
  FREE: "Free",
  REIMBURSED: "Paid-reimbursed",
  NOT_REIMBURSED: "Paid-not reimbursed",
};

export const parkingInstructionsMaxLength = 5000;
export const parkingInstructionsFieldsSchema = z.object({
  parking_expense_type: z.nullish(z._default(parkingExpenseTypeSchema, "FREE")),
  parking_instructions: z.nullish(
    z.pipe(
      z.string().check(z.maxLength(parkingInstructionsMaxLength)),
      z.transform((v) => v || undefined),
    ),
  ),
});
export type ParkingInstructions = z.infer<typeof parkingInstructionsFieldsSchema>;

export const attachmentTypeSchema = z.enum(GigAttachmentTypeEnum);

const attachmentStatusSchema = z.enum(["available", "deleted", "pending", "rejected"]);

export const fileAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: z.string(),
  provider_identifier: z.string(),
  created_ts: z.coerce.date(),
  status: attachmentStatusSchema,
  resource_url: z.url(),
  type: z._default(attachmentTypeSchema, "DEFAULT"),
});
export type FileAttachment = z.infer<typeof fileAttachmentSchema>;

export const ratingTypeSchema = z.object({
  rank: z.coerce.number(),
  name: z.string(),
  label: z.string(),
  is_overall_type: z.boolean(),
});

export const ratingSummarySchema = z.extend(ratingTypeSchema, {
  avg_rating: z.nullable(z.coerce.number()),
  rating_count: z.coerce.number(),
});

export const invoiceFrequencySchema = z.enum(["WEEKLY", "DAILY"]);

export const weekdaySchema = z.enum(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]);
export type Weekday = z.infer<typeof weekdaySchema>;

export const invoiceStyleSchema = z.enum(["STANDARD", "LOCATION", "ADDRESS"]);

export const invoicingAutomationLevelSchema = z.enum(["NONE", "DRAFT", "CREATE", "SEND"]);

export const netTermsSchema = z.enum(["NET_15", "NET_30", "NET_45", "NET_60", "NET_75", "AUTOPAY"]);

export const netTermsFeeSchema = z.enum(["0%", "5%"]);

export const locationPaymentSourceSchema = z.enum(["INVOICING", "INSTANT_CHARGES", "ORGANIZATION", "NONE"]);

export const orgPaymentSourceSchema = z.enum(["INVOICING", "INSTANT_CHARGES", "NONE"]);

/** Reasons a manager can select when cancelling or dismissing */
const managerCancellationReasonSchema = z.enum(["NOT_NEEDED", "PRO_ISSUE", "OTHER"]);
export type ManagerCancellationReason = z.infer<typeof managerCancellationReasonSchema>;

/** Admin-only cancellation reason: the pro is still paid, the business is not charged */
const adminCancellationReasonSchema = z.enum(["ADMIN_WAIVER"]);
export type AdminCancellationReason = z.infer<typeof adminCancellationReasonSchema>;

/**
 * Application-level only, and only on a shift Qwick overfilled (WORK-7598): the pro is still paid
 * the 4-hour minimum and the business is not charged. There is no gig-level equivalent — cancelling
 * a whole shift with this reason is rejected by the API — so it stays out of
 * `gigCancellationReasonSchema` on purpose.
 */
const overfillCancellationReasonSchema = z.enum(["QWICK_OVERFILL"]);
export type OverfillCancellationReason = z.infer<typeof overfillCancellationReasonSchema>;

/** Deprecated gig cancellation reasons — no longer in UI but still returned by the API */
const legacyGigCancellationReasonSchema = z.enum(["CALLED_OUT", "CORRECTION", "NO_SHOW", "REPOST"]);

/** Deprecated dismissal reasons — no longer in UI but still returned by the API */
const legacyDismissalReasonSchema = z.enum([
  "BUSINESS_CALLED_OUT",
  "FEWER_PROS_NEEDED",
  "NO_SHOW",
  "OVERRIDE_AUTOSELECT",
  "STAFFER_DISMISSAL",
]);

/**
 * Platform-generated cancellation reasons (not user-initiated).
 *
 * CONFIRMED_ELSEWHERE and MISSING_REQUIREMENTS are emitted when a direct offer could not be sent;
 * they are read-only, so they stay out of both write schemas.
 */
const platformCancellationReasonSchema = z.enum([
  "CANCELLED_BY_GIGPRO_ADMIN",
  "CANCELLED_BY_GIGPRO_LOCATION_MANAGER",
  "CANCELLED_BY_PRO",
  "CONFIRMED_ELSEWHERE",
  "MISSING_REQUIREMENTS",
  "PRO_BLOCKED",
  "PRO_SUSPENDED",
  "QWICK_OVERFILL",
  "ADMIN_WAIVER",
]);

/** All reasons when cancelling a gig (write — staffer-initiated) */
export const gigCancellationReasonSchema = z.enum([
  ...managerCancellationReasonSchema.options,
  ...adminCancellationReasonSchema.options,
  ...legacyGigCancellationReasonSchema.options,
]);
export type GigCancellationReason = z.infer<typeof gigCancellationReasonSchema>;

/**
 * The valid-reasons endpoint is the authority on which reasons are legal, and its set is wider than
 * the generated client's enum: ADMIN_WAIVER (WORK-7597) is accepted over the wire today but only
 * reaches `GigCancellationReasonEnum` with the next OpenAPI regen. Pass the server's own value
 * straight back rather than re-deriving it locally.
 *
 * TODO(WORK-7595): narrow the parameter back to `GigCancellationReason` once the regen lands.
 */
export const toApiGigCancellationReason = (reason: string) => reason as GigCancellationReasonEnum;

/**
 * App-level counterpart to `toApiGigCancellationReason`. ADMIN_WAIVER (WORK-7597) and
 * QWICK_OVERFILL (WORK-7598) are accepted over the wire today but only reach
 * `ApplicationCancellationReasonEnum` with the next OpenAPI regen.
 *
 * TODO(WORK-7595): drop this once the regen lands.
 */
export const toApiApplicationCancellationReason = (reason: string) => reason as ApplicationCancellationReasonEnum;

/**
 * All reasons when dismissing a pro from a gig (write — staffer-initiated).
 *
 * Wider than the gig-level set: this is the only level that accepts QWICK_OVERFILL. Which of these
 * a given actor may actually pick is decided by the app-level valid-reasons endpoint, not here.
 */
export const proDismissalReasonSchema = z.enum([
  ...managerCancellationReasonSchema.options,
  ...adminCancellationReasonSchema.options,
  ...overfillCancellationReasonSchema.options,
  ...legacyDismissalReasonSchema.options,
]);
export type DismissalReason = z.infer<typeof proDismissalReasonSchema>;

/**
 * All possible cancellation reasons on an application response (read).
 * Includes everything the API can return to avoid validation errors.
 */
export const applicationCancellationReasonSchema = z.enum([
  ...gigCancellationReasonSchema.options,
  ...overfillCancellationReasonSchema.options,
  ...legacyDismissalReasonSchema.options,
  ...platformCancellationReasonSchema.options,
]);
export type ApplicationCancellationReason = z.infer<typeof applicationCancellationReasonSchema>;
